import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export interface GroundRentRecord {
  id: string;
  fixed_asset_id: string;
  annual_amount: number;
  due_month: number;
  due_day: number;
  start_year: number;
  notes: string | null;
  currency: string;
  created_at: string;
  updated_at: string;
}

export interface GroundRentPayment {
  id: string;
  ground_rent_id: string;
  user_id: string;
  year_paid: number;
  amount: number;
  payment_date: string;
  receipt_number: string | null;
  notes: string | null;
  transaction_id: string | null;
  created_at: string;
}

export function useGroundRent(fixedAssetId: string | undefined) {
  return useQuery({
    queryKey: ['ground-rent', fixedAssetId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('ground_rent_records')
        .select('*')
        .eq('fixed_asset_id', fixedAssetId!)
        .maybeSingle();
      if (error) throw error;
      return data as GroundRentRecord | null;
    },
    enabled: !!fixedAssetId,
  });
}

export function useGroundRentPayments(groundRentId: string | undefined) {
  return useQuery({
    queryKey: ['ground-rent-payments', groundRentId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('ground_rent_payments')
        .select('*')
        .eq('ground_rent_id', groundRentId!)
        .order('year_paid', { ascending: false });
      if (error) throw error;
      return data as GroundRentPayment[];
    },
    enabled: !!groundRentId,
  });
}

export function useCreateGroundRent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (record: Omit<GroundRentRecord, 'id' | 'created_at' | 'updated_at'>) => {
      const { data, error } = await supabase
        .from('ground_rent_records')
        .insert(record)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: ['ground-rent', variables.fixed_asset_id] });
    },
  });
}

export function useUpdateGroundRent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<GroundRentRecord> & { id: string }) => {
      const { data, error } = await supabase
        .from('ground_rent_records')
        .update(updates)
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['ground-rent', data.fixed_asset_id] });
    },
  });
}

export function useRecordGroundRentPayment() {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (payment: Omit<GroundRentPayment, 'id' | 'created_at' | 'user_id'>) => {
      const { data, error } = await supabase
        .from('ground_rent_payments')
        .insert({ ...payment, user_id: user!.id })
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: ['ground-rent-payments', variables.ground_rent_id] });
    },
  });
}

export function useDeleteGroundRentPayment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ paymentId }: { paymentId: string }) => {
      const { error } = await supabase
        .from('ground_rent_payments')
        .delete()
        .eq('id', paymentId);
      if (error) throw error;
    },
    onSuccess: (_, variables) => {
      // Invalidate everything ground rent related to be safe
      qc.invalidateQueries({ queryKey: ['ground-rent-payments'] });
    },
  });
}
